import { Group, List, Loader, Stack, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute } from '@tanstack/react-router'
import { useAccount } from 'wagmi'

import {
  IconCheck,
  IconFailed,
  IconQuestionMark,
} from '@repo/common/components/Icons'
import { usdLargeNumberFormatter } from '@repo/common/helpers/formatters'
import { useAuth } from '@repo/common/queries/auth'
import { useCheckPermit2 } from '@repo/common/queries/blockchain/useSetupPermit2'
import {
  useGetFundLatestNavPrice,
  useGetFundPrices,
  useGetMinterDisabled,
  useGetMinterLimitLeft,
} from '@repo/common/queries/funds'
import { amount_to_tokens } from '@repo/common/queries/helpers/block_helpers'
import { useGetKycStatus } from '@repo/common/queries/kyc'
import {
  useGetIpData,
  useGetLocationAttestation,
} from '@repo/common/queries/location'
import {
  useCheckIsRestricted,
  useCheckKYCLevel,
  useGetSDDLimits,
} from '@repo/common/queries/token_auth'
import {
  useGetVaultDepositLimitLeft,
  useGetVaultDisabled,
} from '@repo/common/queries/vaults'
import { useGetWalletRiskCheck } from '@repo/common/queries/wallet'

import { ConnectWalletButton } from '@/components/ConnectWalletButton'
import { useIsMismatchedWallet } from '@/panels/PanelRegisterWallet'

export const Route = createFileRoute('/_app/checks')({
  component: AuthChecksPage,
})

// eslint-disable-next-line max-lines-per-function, complexity
function AuthChecksPage() {
  const kyc_query = useGetKycStatus()
  const location_query = useGetLocationAttestation()
  const ip_query = useGetIpData()
  const permit_usdc_query = useCheckPermit2({ symbol: 'USDC' })
  const permit_tokx_query = useCheckPermit2({ symbol: 'TOKX' })
  const mismatch = useIsMismatchedWallet()
  const wallet_risk_check_query = useGetWalletRiskCheck()
  const auth = useAuth()
  const restricted_tokx_query = useCheckIsRestricted({ symbol: 'TOKX' })
  const restricted_xvlt_query = useCheckIsRestricted({ symbol: 'XVLT' })
  const kyc_level_query = useCheckKYCLevel()
  const sdd_limits_tokx = useGetSDDLimits({ symbol: 'TOKX' })
  const sdd_limits_xvlt = useGetSDDLimits({ symbol: 'XVLT' })
  const tokx_limit_query = useGetMinterLimitLeft({ symbol: 'TOKX' })
  const xvlt_limit_query = useGetVaultDepositLimitLeft({ symbol: 'XVLT' })
  const tokx_prices_query = useGetFundPrices({ symbol: 'TOKX' })
  const tokx_nav_price_query = useGetFundLatestNavPrice({ symbol: 'TOKX' })
  const tokx_disabled_query = useGetMinterDisabled({ symbol: 'TOKX' })
  const xvlt_deposit_disabled_query = useGetVaultDisabled({
    symbol: 'XVLT',
    type: 'mint',
  })
  const xvlt_redeem_disabled_query = useGetVaultDisabled({
    symbol: 'XVLT',
    type: 'redeem',
  })
  const { address } = useAccount()

  if (address == null) {
    return (
      <Stack>
        <Title>Auth Checks</Title>
        <Group>
          <ConnectWalletButton
            size="lg"
            variant="light"
            message="Connect Wallet First"
          />
        </Group>
      </Stack>
    )
  }

  return (
    <Stack>
      <Title>Checks</Title>

      <Title order={2}>Your Backend Checks</Title>
      <List center spacing="xs">
        <LineItem
          isLoading={auth.isLoading}
          passed={auth.data?.is_email_confirmed}
        >
          Email confirmed? {ConditionText(auth.data?.is_email_confirmed)}
        </LineItem>
        <br />
        <LineItem
          isLoading={!mismatch.isSuccess}
          passed={mismatch.registered_address != null}
        >
          Wallet Registered?{' '}
          {ConditionText(mismatch.registered_address != null)}
        </LineItem>
        <LineItem isLoading={!mismatch.isSuccess} passed={mismatch.is_matching}>
          Wallet Register Matches connect? {ConditionText(mismatch.is_matching)}
        </LineItem>
        <LineItem
          isLoading={wallet_risk_check_query.isLoading}
          passed={wallet_risk_check_query.data?.passed}
        >
          Wallet Risk Check Passed?{' '}
          {ConditionText(wallet_risk_check_query.data?.passed)}
        </LineItem>
        <br />
        <LineItem
          isLoading={kyc_query.isLoading}
          passed={kyc_query.data?.passed}
        >
          KYC Passed? {ConditionText(kyc_query.data?.passed)}
        </LineItem>
        <LineItem
          isLoading={kyc_query.isLoading}
          passed={kyc_query.data?.level}
        >
          KYC Level? {kyc_query.data?.level}
        </LineItem>
        <LineItem
          isLoading={kyc_query.isLoading}
          passed={kyc_query.data?.market}
        >
          KYC Market? {kyc_query.data?.market}
        </LineItem>
        <LineItem
          isLoading={kyc_query.isLoading}
          passed={kyc_query.data?.applicant_type}
        >
          KYC Applicant Type? {kyc_query.data?.applicant_type}
        </LineItem>
        <br />
        <LineItem
          isLoading={location_query.isLoading}
          passed={location_query.data?.passed}
        >
          Location Attestation & Check Passed?{' '}
          {ConditionText(location_query.data?.passed)}
        </LineItem>
        <LineItem isLoading={ip_query.isLoading} passed={ip_query.data != null}>
          Country via IP data? - {ip_query.data?.country_name} (
          {ip_query.data?.country_code}) {ip_query.data?.emoji_flag}
        </LineItem>
        <LineItem isLoading={ip_query.isLoading} passed={ip_query.data != null}>
          GeoLocation data? - {ip_query.data?.latitude}{' '}
          {ip_query.data?.longitude}
        </LineItem>
      </List>
      <Title order={2}>Your Blockchain Checks</Title>
      <List center spacing="xs">
        <LineItem
          isLoading={kyc_level_query.isLoading}
          passed={kyc_level_query.data != 'none'}
        >
          On Chain KYC Level? - {kyc_level_query.data ?? 'none'}
        </LineItem>
        <br />
        <LineItem
          isLoading={sdd_limits_tokx.isSuccess == false}
          passed={sdd_limits_tokx.purchases_left_today ?? 0 > 0}
        >
          TOKX SDD purchases left for today:{' '}
          {sdd_limits_tokx.purchases_left_today}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_tokx.isSuccess == false}
          passed={sdd_limits_tokx.purchases_left_today ?? 0 > 0}
        >
          TOKX SDD purchases amount left for today:{' '}
          {sdd_limits_tokx.purchases_tokens_left_today}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_tokx.isSuccess == false}
          passed={sdd_limits_tokx.purchases_left_duration ?? 0 > 0}
        >
          TOKX SDD purchases left for duration:{' '}
          {sdd_limits_tokx.purchases_left_duration}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_tokx.isSuccess == false}
          passed={sdd_limits_tokx.purchases_left_today ?? 0 > 0}
        >
          TOKX SDD purchases amount left for duration:{' '}
          {sdd_limits_tokx.purchases_tokens_left_duration}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_tokx.isSuccess == false}
          passed={sdd_limits_tokx.purchases_left_today ?? 0 > 0}
        >
          TOKX SDD today&apos;s last purchase was:{' '}
          {sdd_limits_tokx.last_todays_purchase_time == null
            ? 'n/a'
            : sdd_limits_tokx.last_todays_purchase_time.toISOString()}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_tokx.isSuccess == false}
          passed={sdd_limits_tokx.purchases_left_duration ?? 0 > 0}
        >
          TOKX SDD duration&apos;s last purchase was:{' '}
          {sdd_limits_tokx.last_duration_purchase_time == null
            ? 'n/a'
            : sdd_limits_tokx.last_duration_purchase_time.toISOString()}
        </LineItem>
        <br />
        <LineItem
          isLoading={sdd_limits_xvlt.isSuccess == false}
          passed={sdd_limits_xvlt.purchases_left_today ?? 0 > 0}
        >
          XVLT SDD purchases left for today:{' '}
          {sdd_limits_xvlt.purchases_left_today}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_xvlt.isSuccess == false}
          passed={sdd_limits_xvlt.purchases_left_today ?? 0 > 0}
        >
          XVLT SDD purchases amount left for today:{' '}
          {sdd_limits_xvlt.purchases_tokens_left_today}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_xvlt.isSuccess == false}
          passed={sdd_limits_xvlt.purchases_left_duration ?? 0 > 0}
        >
          XVLT SDD purchases left for duration:{' '}
          {sdd_limits_xvlt.purchases_left_duration}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_xvlt.isSuccess == false}
          passed={sdd_limits_xvlt.purchases_left_today ?? 0 > 0}
        >
          XVLT SDD purchases amount left for duration:{' '}
          {sdd_limits_xvlt.purchases_tokens_left_duration}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_xvlt.isSuccess == false}
          passed={sdd_limits_xvlt.purchases_left_today ?? 0 > 0}
        >
          XVLT SDD today&apos;s last purchase was:{' '}
          {sdd_limits_xvlt.last_todays_purchase_time == null
            ? 'n/a'
            : sdd_limits_xvlt.last_todays_purchase_time.toISOString()}
        </LineItem>
        <LineItem
          isLoading={sdd_limits_xvlt.isSuccess == false}
          passed={sdd_limits_xvlt.purchases_left_duration ?? 0 > 0}
        >
          XVLT SDD duration&apos;s last purchase was:{' '}
          {sdd_limits_xvlt.last_duration_purchase_time == null
            ? 'n/a'
            : sdd_limits_xvlt.last_duration_purchase_time.toISOString()}
        </LineItem>
        <br />
        <LineItem
          isLoading={permit_usdc_query.isLoading}
          passed={permit_usdc_query.data?.permit_enabled}
        >
          Permit2 for USDC granted?{' '}
          {ConditionText(
            permit_usdc_query.data?.permit_enabled,
            usdLargeNumberFormatter(
              amount_to_tokens(
                permit_usdc_query.data?.token_allowance || 0n,
                6,
              ),
            ),
          )}
        </LineItem>
        <LineItem
          isLoading={permit_tokx_query.isLoading}
          passed={permit_tokx_query.data?.permit_enabled}
        >
          Permit2 for TOKX granted?{' '}
          {ConditionText(
            permit_tokx_query.data?.permit_enabled,
            usdLargeNumberFormatter(
              amount_to_tokens(
                permit_tokx_query.data?.token_allowance || 0n,
                6,
              ),
            ),
          )}
        </LineItem>
      </List>
      <Title order={2}>System Blockchain Checks</Title>
      <List center spacing="xs">
        <LineItem
          isLoading={!tokx_disabled_query.isSuccess}
          passed={!tokx_disabled_query.disabled}
        >
          TOKX disabled reason: {tokx_disabled_query.reason}
        </LineItem>
        <LineItem
          isLoading={!xvlt_deposit_disabled_query.isSuccess}
          passed={!xvlt_deposit_disabled_query.disabled}
        >
          XVLT Deposit disabled reason: {xvlt_deposit_disabled_query.reason}
        </LineItem>
        <LineItem
          isLoading={!xvlt_redeem_disabled_query.isSuccess}
          passed={!xvlt_redeem_disabled_query.disabled}
        >
          XVLT Redeem disabled reason: {xvlt_redeem_disabled_query.reason}
        </LineItem>
        <br />
        <LineItem
          isLoading={tokx_limit_query.isLoading}
          passed={tokx_limit_query.data}
        >
          TOKX Mint Limit Left: {usdLargeNumberFormatter(tokx_limit_query.data)}
        </LineItem>
        <LineItem
          isLoading={xvlt_limit_query.isLoading}
          passed={xvlt_limit_query.data}
        >
          XVLT Mint Limit Left: {usdLargeNumberFormatter(xvlt_limit_query.data)}
        </LineItem>
        <br />
        <LineItem
          isLoading={tokx_prices_query.isLoading}
          passed={tokx_prices_query.data}
        >
          TOKX Dex Price: {usdLargeNumberFormatter(tokx_prices_query.data?.dex)}
        </LineItem>
        <LineItem
          isLoading={tokx_prices_query.isLoading}
          passed={tokx_prices_query.data}
        >
          TOKX Oracle Price:{' '}
          {usdLargeNumberFormatter(tokx_prices_query.data?.oracle)}
        </LineItem>
        <LineItem
          isLoading={tokx_nav_price_query.isLoading}
          passed={tokx_nav_price_query.data}
        >
          TOKX Nav Price: {usdLargeNumberFormatter(tokx_nav_price_query.data)}
        </LineItem>
        <LineItem
          isLoading={tokx_prices_query.isLoading}
          passed={tokx_prices_query.data}
        >
          TOKX Observation Price:{' '}
          {usdLargeNumberFormatter(tokx_prices_query.data?.observation)}
        </LineItem>
        <LineItem
          isLoading={tokx_prices_query.isLoading}
          passed={tokx_prices_query.data}
        >
          TOKX Mint Price:{' '}
          {usdLargeNumberFormatter(tokx_prices_query.data?.mint)}
        </LineItem>
        <LineItem
          isLoading={tokx_prices_query.isLoading}
          passed={tokx_prices_query.data}
        >
          TOKX Mint Floor Price:{' '}
          {usdLargeNumberFormatter(tokx_prices_query.data?.mint_floor)}
        </LineItem>
        <br />
        <LineItem
          isLoading={restricted_tokx_query.isLoading}
          passed={!restricted_tokx_query.data?.mint}
        >
          TOKX Mint enabled? {ConditionText(!restricted_tokx_query.data?.mint)}
        </LineItem>
        <LineItem
          isLoading={restricted_tokx_query.isLoading}
          passed={!restricted_tokx_query.data?.mint_for_sdd}
        >
          TOKX Mint for SDD enabled?{' '}
          {ConditionText(!restricted_tokx_query.data?.mint_for_sdd)}
        </LineItem>
        <LineItem
          isLoading={restricted_tokx_query.isLoading}
          passed={!restricted_tokx_query.data?.trade_fdex}
        >
          TOKX Dex Trading enabled?{' '}
          {ConditionText(!restricted_tokx_query.data?.trade_fdex)}
        </LineItem>
        <LineItem
          isLoading={restricted_tokx_query.isLoading}
          passed={!restricted_tokx_query.data?.trade_other}
        >
          TOKX Other Trading enabled?{' '}
          {ConditionText(!restricted_tokx_query.data?.trade_other)}
        </LineItem>
        <br />
        <LineItem
          isLoading={restricted_xvlt_query.isLoading}
          passed={!restricted_xvlt_query.data?.mint}
        >
          XVLT Mint enabled? {ConditionText(!restricted_xvlt_query.data?.mint)}
        </LineItem>
        <LineItem
          isLoading={restricted_xvlt_query.isLoading}
          passed={!restricted_xvlt_query.data?.mint_for_sdd}
        >
          XVLT Mint for SDD enabled?{' '}
          {ConditionText(!restricted_xvlt_query.data?.mint_for_sdd)}
        </LineItem>
        <LineItem
          isLoading={restricted_xvlt_query.isLoading}
          passed={!restricted_xvlt_query.data?.trade_fdex}
        >
          XVLT Dex Trading enabled?{' '}
          {ConditionText(!restricted_xvlt_query.data?.trade_fdex)}
        </LineItem>
        <LineItem
          isLoading={restricted_xvlt_query.isLoading}
          passed={!restricted_xvlt_query.data?.trade_other}
        >
          XVLT Other Trading enabled?{' '}
          {ConditionText(!restricted_xvlt_query.data?.trade_other)}
        </LineItem>
      </List>
    </Stack>
  )
}

// eslint-disable-next-line max-params
function ConditionText(
  condition?: unknown,
  yes: string = 'yes',
  no: string = 'no',
) {
  if (condition == null) return null
  return ` - ${condition ? yes : no}`
}

function LineItem({
  children,
  isLoading,
  passed,
}: {
  children: React.ReactNode
  isLoading: boolean
  passed?: unknown
}) {
  let icon
  if (isLoading) {
    icon = <Loader color="blue" size="24px" />
  } else if (passed) {
    icon = (
      <ThemeIcon color="green">
        <IconCheck width="24px" />
      </ThemeIcon>
    )
  } else if (passed === false) {
    icon = (
      <ThemeIcon color="red">
        <IconFailed width="24px" />
      </ThemeIcon>
    )
  } else {
    icon = (
      <ThemeIcon color="orange">
        <IconQuestionMark width="24px" />
      </ThemeIcon>
    )
  }

  return <List.Item icon={icon}>{children}</List.Item>
}
